/**
 * Projects List Page
 * Shows all projects with search, filters, and table/grid views
 */

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  LayoutGrid, 
  List,
  FolderOpen,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { type DBProject, type ProjectStatus } from '../db';
import { projectsApi } from '../lib/api';
import { ProjectCard } from '../components/projects/ProjectCard';
import { ProjectRow } from '../components/projects/ProjectRow';
import { NewProjectModal } from '../components/projects/NewProjectModal';
import { ProjectFilters } from '../components/projects/ProjectFilters';

type ViewMode = 'grid' | 'table';

const ITEMS_PER_PAGE = 12;

export function ProjectsList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<DBProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    loadProjects();
  }, [refreshKey]);

  // Listen for project updates from other components
  useEffect(() => {
    const handleProjectsUpdated = () => {
      forceRefresh();
    };
    window.addEventListener('projects-updated', handleProjectsUpdated);
    return () => window.removeEventListener('projects-updated', handleProjectsUpdated);
  }, []);

  const forceRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await projectsApi.list({ limit: 1000 });
      const projectsData = response.data?.data;
      
      if (!projectsData || !Array.isArray(projectsData)) {
        setProjects([]);
        return;
      }
      
      setProjects(projectsData);
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter projects based on search and status
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      // Status filter
      if (statusFilter !== 'all' && project.status !== statusFilter) {
        return false;
      }
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          project.code.toLowerCase().includes(query) ||
          project.name.toLowerCase().includes(query) ||
          project.line.toLowerCase().includes(query)
        );
      }
      
      return true;
    });
  }, [projects, searchQuery, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);
  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProjects.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProjects, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const handleProjectCreated = async (projectId: string) => {
    setIsNewProjectModalOpen(false);
    // Force reload projects list
    forceRefresh();
    // Navigate to the new project
    navigate(`/projects/${projectId}`);
  };

  const handleDeleteProject = async (projectId: string) => {
    // Immediately remove from local state for instant feedback
    setProjects(prev => prev.filter(p => p.id !== projectId));
    // Force reload to get updated list from server
    forceRefresh();
  };

  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-slate-300 dark:bg-slate-700/50 rounded w-48 mb-6" />
        <div className="h-12 bg-slate-300 dark:bg-slate-700/50 rounded mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-48 bg-slate-300 dark:bg-slate-700/50 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-mono font-bold text-slate-800 dark:text-slate-100 flex items-center gap-3 tracking-wide">
            <FolderOpen className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            <span>PROJECTS</span>
            <span className="text-blue-600 dark:text-blue-400">DATABASE</span>
          </h1>
          <p className="font-mono text-xs text-slate-500 mt-1 ml-10">
            {projects.length} records :: {filteredProjects.length} displayed
          </p>
        </div>
        <button
          onClick={() => setIsNewProjectModalOpen(true)}
          className="action-btn border-l-3 border-l-blue-500 px-4 py-2 font-mono font-medium text-slate-700 dark:text-slate-200 flex items-center gap-2 text-sm"
        >
          <Plus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          New Project
        </button>
      </div>

      {/* Filters Bar */}
      <div className="stat-card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="$ search_query..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg font-mono text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-colors"
            />
          </div>

          {/* Status Filter */}
          <ProjectFilters 
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
          />

          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800/50 rounded-lg p-1 border border-slate-200 dark:border-slate-700/50">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
              title="Grid view"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'table' 
                  ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
              title="Table view"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Projects List */}
      {filteredProjects.length === 0 ? (
        <div className="stat-card p-12 text-center">
          <FolderOpen className="w-16 h-16 text-slate-400 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-mono font-medium text-slate-700 dark:text-slate-300 mb-2">
            {projects.length === 0 ? 'NO PROJECTS FOUND' : 'NO MATCHING RESULTS'}
          </h3>
          <p className="font-mono text-sm text-slate-500 mb-6">
            {projects.length === 0 
              ? '$ init_project --new'
              : '$ adjust_filters --retry'
            }
          </p>
          {projects.length === 0 && (
            <button
              onClick={() => setIsNewProjectModalOpen(true)}
              className="action-btn border-l-3 border-l-blue-500 px-4 py-2 font-mono font-medium text-slate-700 dark:text-slate-200 inline-flex items-center gap-2 text-sm"
            >
              <Plus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              New Project
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {paginatedProjects.map(project => (
            <ProjectCard 
              key={project.id} 
              project={project}
              onDelete={handleDeleteProject}
            />
          ))}
          {/* New Project Card */}
          <button
            onClick={() => setIsNewProjectModalOpen(true)}
            className="project-card border-2 border-dashed border-slate-300 dark:border-slate-600 p-6 flex flex-col items-center justify-center gap-3 hover:border-blue-400 dark:hover:border-blue-500/50 transition-colors min-h-[200px]"
          >
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-200 dark:border-blue-500/30">
              <Plus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="font-mono font-medium text-slate-500 dark:text-slate-400 text-sm">New Project</span>
          </button>
        </div>
      ) : (
        <div className="stat-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-mono font-medium text-slate-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-mono font-medium text-slate-500 uppercase tracking-wider">Line</th>
                <th className="px-4 py-3 text-center text-xs font-mono font-medium text-slate-500 uppercase tracking-wider">Photos</th>
                <th className="px-4 py-3 text-center text-xs font-mono font-medium text-slate-500 uppercase tracking-wider">Anomalies</th>
                <th className="px-4 py-3 text-center text-xs font-mono font-medium text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-mono font-medium text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700/50">
              {paginatedProjects.map(project => (
                <ProjectRow 
                  key={project.id} 
                  project={project}
                  onDelete={handleDeleteProject}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-xs font-mono text-slate-500 tabular-nums">
            Displaying {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredProjects.length)} of {filteredProjects.length} records
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 rounded-lg text-sm font-mono font-medium transition-colors ${
                  currentPage === page
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* New Project Modal */}
      <NewProjectModal
        isOpen={isNewProjectModalOpen}
        onClose={() => setIsNewProjectModalOpen(false)}
        onProjectCreated={handleProjectCreated}
      />
    </div>
  );
}
